'use client'

import { useState, useEffect, useRef } from 'react'

interface UseTypewriterOptions {
  typingSpeed?: number      // ms per character (default: 80)
  deletingSpeed?: number    // ms per character (default: 40)
  pauseDuration?: number    // ms to pause when complete (default: 2500)
  paused?: boolean          // pause the animation
}

type Phase = 'typing' | 'pausing' | 'deleting'

export function useTypewriter(
  texts: string[],
  options: UseTypewriterOptions = {}
): string {
  const {
    typingSpeed = 80,
    deletingSpeed = 40,
    pauseDuration = 2500,
    paused = false,
  } = options

  const [displayText, setDisplayText] = useState('')
  const [textIndex, setTextIndex] = useState(0)
  const [phase, setPhase] = useState<Phase>('typing')
  const charIndexRef = useRef(0)

  useEffect(() => {
    if (paused || texts.length === 0) {
      return
    }

    const currentText = texts[textIndex]

    if (phase === 'typing') {
      if (charIndexRef.current < currentText.length) {
        const timeout = setTimeout(() => {
          charIndexRef.current += 1
          setDisplayText(currentText.slice(0, charIndexRef.current))
        }, typingSpeed)
        return () => clearTimeout(timeout)
      } else {
        // Finished typing, start pausing
        setPhase('pausing')
      }
    }

    if (phase === 'pausing') {
      const timeout = setTimeout(() => {
        setPhase('deleting')
      }, pauseDuration)
      return () => clearTimeout(timeout)
    }

    if (phase === 'deleting') {
      if (charIndexRef.current > 0) {
        const timeout = setTimeout(() => {
          charIndexRef.current -= 1
          setDisplayText(currentText.slice(0, charIndexRef.current))
        }, deletingSpeed)
        return () => clearTimeout(timeout)
      } else {
        // Finished deleting, move to next text
        setTextIndex((prev) => (prev + 1) % texts.length)
        setPhase('typing')
      }
    }
  }, [displayText, phase, textIndex, texts, typingSpeed, deletingSpeed, pauseDuration, paused])

  return displayText
}

